import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '@Contexts';
import { UserTypeEnum } from '@Types';
import {
  GroupProfileController,
  FunderProfileController,
  OrganizationController,
  DocumentsController,
  BankDetailsController,
  TeamMembersController,
} from '@Views';
import { useTranslation } from 'react-i18next';
import { AccountNav } from './Components/Nav';
import { PathParams, Routes } from '@App/paths';
import router from 'next/router';
import { useGetCurrentTab } from '@Hooks/useGetCurrentTab';
import { useRouter } from '@Helpers';
import { Button } from '@Components/common';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useTheme, useMediaQuery } from '@material-ui/core';
import { Title } from '@Components/AccountPage/Components/Title';
import { ConfirmModal } from '@Components/ConfirmModal/ConfirmModal';
import { useFormGuard } from '@Hooks/useFormGuard';
import styles from './AccountController.module.scss';

export const AccountController = () => {
  const { t } = useTranslation('account');
  const { params } = useRouter();
  const theme = useTheme();
  const screenXs = useMediaQuery(theme.breakpoints.down('xs'));
  const [navOpen, setNavOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { userData } = useUserContext();

  const isGroup = useMemo(() => userData?.user_type === UserTypeEnum.GROUP, [userData]);

  useEffect(() => {
    setNavOpen(screenXs);
  }, [screenXs]);

  const tabs = useMemo(() => {
    const funderTabs = [
      { title: `${t('tabs.profile.title')}`, slug: 'profile', amount: undefined },
      { title: `${t('tabs.organization.title')}`, slug: 'organization', amount: undefined },
    ];
    // const teamMembersTab = {
    //   title: `${t('tabs.team_members')}`,
    //   slug: 'team-members',
    //   amount: undefined,
    // };

    if (isGroup) {
      return [
        ...funderTabs,
        { title: `${t('tabs.documents.title')}`, slug: 'documents', amount: undefined },
        { title: `${t('tabs.bank_details.title')}`, slug: 'bank-details', amount: undefined },
      ];
    }
    return funderTabs;
  }, [t, isGroup]);

  const currentTab = useGetCurrentTab(params[PathParams.ACCOUNT_SLUG]?.[0], tabs);

  const { attemptNavigation, confirmationModal } = useFormGuard({
    isDirty: hasUnsavedChanges,
  });

  const handleTabChange = useCallback(
    (tabNumber: number) => {
      const navigate = () => {
        const route = Routes.ACCOUNT(tabs[tabNumber].slug);
        setNavOpen(false);
        router.push(route, undefined, { shallow: true });
      };

      // Only check navigation guard for Documents tab.
      if (currentTab === 2 && isGroup) {
        return attemptNavigation(navigate);
      }

      navigate();
    },
    [currentTab, tabs, isGroup, attemptNavigation],
  );

  const handleOpenNav = useCallback(() => {
    setNavOpen(true);
  }, []);

  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return (
          <>
            <Title title={t('tabs.profile.title')} />
            {userData?.user_type === UserTypeEnum.GROUP ? (
              <GroupProfileController />
            ) : (
              <FunderProfileController />
            )}
          </>
        );
      case 1:
        return (
          <>
            <Title title={t('tabs.organization.title')} isPrivate />
            <OrganizationController />
          </>
        );
      case 2:
        if (isGroup) {
          return (
            <>
              <Title title={t('tabs.documents.title')} isPrivate />
              <DocumentsController onFormStateChange={setHasUnsavedChanges} />
            </>
          );
        }
        return null;
      case 3:
        if (isGroup) {
          return (
            <>
              <Title title={t('tabs.bank_details.title')} isPrivate />
              <BankDetailsController />
            </>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      <AccountNav
        accountNavItems={tabs}
        currentTab={currentTab}
        setTab={handleTabChange}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
      />
      <div className={styles.accountContainer}>
        {screenXs && (
          <Button buttonType="text" onClick={handleOpenNav} startIcon={<RiArrowLeftLine />}>
            {t('back')}
          </Button>
        )}
        {renderTabContent()}
      </div>

      <ConfirmModal
        isOpen={confirmationModal.isOpen}
        onClose={confirmationModal.onCancel}
        title={confirmationModal.title}
        cancelText={confirmationModal.cancelText}
        onCancel={confirmationModal.onCancel}
        confirmText={confirmationModal.confirmText}
        onConfirm={confirmationModal.onConfirm}
        isConfirmBtnDanger
        compact
      >
        {confirmationModal.message}
      </ConfirmModal>
    </>
  );
};
